import { useEffect, useState } from "react";
import axios from "axios";
import { Switch } from "@headlessui/react";
import { config } from "../../utils/config";
import { getCookie } from "../../utils/cookieHandler";
import {
  disabledIfFieldChecker,
  resolveFieldValue,
} from "../../utils/helperFuctions";

const RelatedCardToggles = ({
  label,
  id,
  body,
  name,
  url,
  disabled,
  handleChange,
  handleInputChange,
  fields,
  defaultValue,
  disabledIfField,
  currentFields,
}) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch data from the provided URL
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${config.apiUrl}/api/${url}`, {
          headers: { Authorization: `Bearer ${getCookie("_AT")}` },
        });

        const toggleItems = Array.isArray(response.data)
          ? response.data.map((item) => {
              const matched = Array.isArray(defaultValue)
                ? defaultValue.find((d) => d.vehicle_extra_item === item.id)
                : null;
              return matched
                ? { ...matched, ...item, send: true }
                : { ...item, checked: false };
            })
          : [];

        setItems(toggleItems);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (url) {
      fetchData();
    }
    // Add defaultValue as dependency if it can change after mount
  }, [url]);

  // Handle toggle change
  const handleToggleChange = (itemId) => {
    const consts = { itemId };

    // To update:
    const updatedItems = items.map((item) => {
      return item.id === itemId
        ? {
            ...item,
            send: true,
            ...Object.fromEntries(
              Object.entries(body).map(([key, value]) => [
                key,
                resolveFieldValue(
                  key,
                  value,
                  fields,
                  id,
                  consts,
                  !item.checked
                ),
              ])
            ),
          }
        : item;
    });

    setItems(updatedItems);

    // Call the parent's handleChange with the updated item
    const changedItem = updatedItems.find((item) => item.id === itemId);
    if (changedItem) {
      const value = [...updatedItems].filter((item) => item.send === true);
      handleInputChange({
        target: {
          name: name,
          value: value,
          related: true,
        },
      });
      handleChange(
        {
          ...changedItem,
          checked: changedItem.checked,
        },
        name
      );
    }
  };

  if (loading)
    return (
      <div className="bg-white rounded-lg shadow p-4">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-1/3"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="flex items-center justify-between p-3 border rounded"
              >
                <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                <div className="h-6 w-12 bg-gray-200 rounded-full"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );

  if (error)
    return (
      <div className="bg-white rounded-lg shadow p-4 text-red-500">
        Error: {error}
      </div>
    );
  const isFieldDisabled =
    disabledIfField != null
      ? disabled ||
        disabledIfFieldChecker(currentFields, {
          field: disabledIfField.field,
          valuesArray: disabledIfField.isEqual,
        })
      : disabled ?? false;

  if (!items.length) return null;

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="border-b border-gray-200 bg-gray-100 px-4 py-5 sm:px-6 rounded-t-lg">
        <h3 className="text-base font-semibold leading-6 text-gray-700">
          Extras items
        </h3>
      </div>
      <div className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
          {items.map((item, idx) => {
            // Find if it's NOT the first col in the row
            // For 3 columns: 0,3,6,... are first col in their row
            const isFirstColLg = idx % 3 === 0;
            const isFirstColMd = idx % 2 === 0;

            let borderClass = "";
            let paddingClass = "";

            if (window.innerWidth >= 1024) {
              // lg breakpoint
              borderClass = !isFirstColLg
                ? "lg:border-l lg:border-gray-200"
                : "";
              paddingClass = !isFirstColLg ? "lg:pl-4" : "";
            } else if (window.innerWidth >= 768) {
              // md breakpoint
              borderClass = !isFirstColMd
                ? "md:border-l md:border-gray-200"
                : "";
              paddingClass = !isFirstColMd ? "md:pl-4" : "";
            }

            return (
              <div
                key={item.id}
                className={`${borderClass} ${paddingClass} flex items-center justify-between py-2 px-4 my-2`}
              >
                <span className="text-sm font-medium text-gray-700 truncate pr-4">
                  {item.name || item.title || `Item ${item.id}`}
                </span>
                <Switch
                  checked={item.checked}
                  disabled={isFieldDisabled}
                  style={{
                    cursor: isFieldDisabled ? "not-allowed" : "pointer",
                    opacity: isFieldDisabled ? "30%" : "100%",
                  }}
                  onChange={() => handleToggleChange(item.id)}
                  className={`${
                    item.checked ? "bg-primary" : "bg-gray-200"
                  } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2`}
                >
                  <span
                    className={`${
                      item.checked ? "translate-x-6" : "translate-x-1"
                    } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                  />
                </Switch>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default RelatedCardToggles;
